// RatioFold — board `ColumnFurniture` `.chip` / `.chip.open` / `.pills`, verbatim values. The chip
// is the ESC pill's build (mono 10 / .12em, hairline, fully round); the pills are today's ratio row,
// summoned rather than standing, so they carry the bar's own ground — over a picture as often as
// over the stage.
//
// The row's own box — how wide it is and where under the bar it aligns — belongs to the host that
// stands it up (`Column.module.scss`, `Sheet.module.scss`); only the pills are shared.
//
// `.chip`'s own dress is `@import`ed from `_chipDress.scss` (`@mixin chip-dress`), shared with
// `WordDoors.module.scss`'s word-door chips — see that partial's own header for why this is a Sass
// `@import`, not a cross-file `composes`.
@import './chipDress';

.row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.pill {
    font-family: var(--tl-font-label);
    font-size: 10px;
    letter-spacing: 0.12em;
    color: var(--tl-muted);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-pill);
    padding: 5px 11px;
    background: color-mix(in srgb, var(--tl-surface) 92%, transparent);
    white-space: nowrap;
    cursor: pointer;
    transition: color 150ms ease, border-color 150ms ease;

    // A mouse-hoverable surface only (`@media (hover: hover)`, same guard `Tile.module.scss` uses for
    // its own hover reveal) — a pill with no hover affordance at all read, operator-tested on a
    // touch/trackpad-less pass, as "can't be hovered"; `.pillOn` restates its own selected look right
    // after so a lit pill never dims toward the unselected hover colour.
    @media (hover: hover) {
        &:hover {
            color: var(--tl-text);
            border-color: var(--tl-border-strong);
        }
    }

    &:focus-visible {
        outline: 1px solid var(--tl-accent);
        outline-offset: 2px;
    }
}

.pillOn {
    color: var(--tl-accent-ink);
    background: var(--tl-accent);
    border-color: var(--tl-accent);

    // `.pill:hover` above is a two-class selector once `:hover` is counted (specificity 0,2,0) — the
    // same as this rule would be bare, so without restating it here a hovered LIT pill would dim from
    // the accent fill toward `.pill:hover`'s own muted-to-text colour. Declared after `.pill`'s own
    // block, so an equal-specificity tie resolves by source order in this rule's favour.
    @media (hover: hover) {
        &:hover {
            color: var(--tl-accent-ink);
            border-color: var(--tl-accent);
        }
    }
}

.chip {
    @include chip-dress;
}

// Pressed while the row is open — the chip says which door is standing open.
.chipOpen {
    color: var(--tl-accent);
    border-color: color-mix(in srgb, var(--tl-accent) 45%, transparent);
    background: color-mix(in srgb, var(--tl-accent) 10%, transparent);
}

// Phone tap target (item 7, wall plan d carried items): 44x44 CSS px minimum. Unlike the header
// glyph and the counter arrows, the row's 6px gap is too tight to hide the growth behind a
// compensating negative margin without adjacent pills' invisible hit areas overlapping each
// other — so this grows the visible chip/pill instead (min-size, not padding alone). Keyed on
// `[data-wall-surface="sheet"]` (`Wall.tsx`'s `isSheetSurface()`, published on `<html>`) rather than
// a width breakpoint (code-quality review F4 / least-and-simplest review §2.3): the floor is a
// question of THUMB, not of window width — a touch tablet in landscape also gets the Sheet and
// needs the same floor a phone does, while a mouse never gets the Sheet regardless of how narrow the
// window is, so the desktop pill grid keeps its shipped density either way.
:global(html[data-wall-surface='sheet']) {
    .pill,
    .chip {
        min-width: $tap-floor;
        min-height: $tap-floor;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
}
